<template>
    <div class="about-us">
        <!-- Floating ContactForm bubble in top right -->
        <div class="floatingContact">
            <ContactForm />
        </div>
        
        <h1>About H.O.M.E.S. Roofing Co.</h1>
        <p class="intro">
            H.O.M.E.S. Roofing Co. is a locally owned and operated roofing company based in Niles, Michigan. 
            We are dedicated to providing high-quality roofing services to residential and commercial customers 
            across the Great Lakes State.
        </p>
        <h2>Our Commitment</h2>
        <ul class="values-list">
            <li>Licensed by the State of Michigan (License #252400025)</li>
            <li>Fully insured with $1,000,000 in general liability coverage</li>
            <li>Free estimates and competitive pricing</li>
            <li>High-quality materials and expert workmanship</li>
            <li>Customer satisfaction guaranteed</li>
        </ul>
        <h2>Services We Offer</h2>
        <ul class="services-list">
            <li>Roof Replacement</li>
            <li>Roof Repair</li>
            <li>Fascia & Soffit Installation</li>
            <li>Gutter Services</li>
            <li>Siding Installation</li>
            <li>Free Roof Inspections</li>
        </ul>
        <div class="cta">
            <p class="cta-text">Ready to get started? Call us today at 269-262-5055 for your free estimate!</p>
            <div class="cta-buttons">
                <a href="tel:269-262-5055" class="cta-phone-btn">
                    📞 Call 269-262-5055
                </a>
                <button class="cta-quote-btn" @click="openQuoteModal">
                    Request A Free Quote
                </button>
            </div>
        </div>

        <h2>Frequently Asked Questions</h2>
        <div class="faq-section">
            <div class="faq-item">
                <h3>How much does a roof replacement cost in Michigan?</h3>
                <p>The cost of a roof replacement in Michigan typically ranges from $7,000 to $25,000+ depending on the size of your home, roofing materials chosen, and complexity of the project. We offer free estimates to provide you with an accurate quote based on your specific needs.</p>
            </div>
            <div class="faq-item">
                <h3>What's included in your 50-year warranty?</h3>
                <p>Our 50-year warranty covers manufacturer defects in roofing materials and workmanship. This comprehensive protection gives you peace of mind knowing your investment is protected for decades to come. Contact us for full warranty details.</p>
            </div>
            <div class="faq-item">
                <h3>Do you offer emergency roof repair services?</h3>
                <p>Yes! We understand that roof damage can happen at any time. Call us at 269-262-5055 for emergency repairs. We respond quickly to prevent further damage to your home.</p>
            </div>
            <div class="faq-item">
                <h3>What roofing materials do you recommend for Michigan weather?</h3>
                <p>For Michigan's harsh winters and humid summers, we prefer architectural asphalt shingles from trusted brands like CertainTeed, IKO, and Owens Corning. These materials offer excellent durability, ice dam resistance, and wind protection. We also install EPDM, TPO, modified bitumen, and we're willing to use other manufacturers' shingles at your request. We'll help you choose the best solution for your home's needs and budget.</p>
            </div>
            <div class="faq-item">
                <h3>How long does a typical roof last in Michigan?</h3>
                <p>In Michigan's climate, a standard asphalt shingle roof typically lasts around 30 years with proper maintenance. However, higher-tier premium shingles and materials can last up to 50 years, offering superior durability and weather resistance. The lifespan depends on factors like material quality, installation, ventilation, and maintenance. We offer a 50-year warranty to protect your investment.</p>
            </div>
            <div class="faq-item">
                <h3>Do you offer financing options?</h3>
                <p>Yes! We partner with financing providers to offer flexible payment plans for projects over $3,000. Apply today to get approved quickly and start your roofing project without delay.</p>
            </div>
            <div class="faq-item">
                <h3>What areas do you serve?</h3>
                <p>We proudly serve <strong>Niles</strong>, <strong>Buchanan</strong>, <strong>Benton Harbor</strong>, <strong>St. Joseph</strong>, <strong>Galien</strong>, <strong>Three Oaks</strong>, <strong>Bridgman</strong>, <strong>New Buffalo</strong>, <strong>Stevensville</strong>, <strong>Coloma</strong>, <strong>Gaylord</strong>, <strong>South Bend</strong>, <strong>Kalamazoo</strong>, <strong>Battle Creek</strong>, <strong>Portage</strong>, <strong>Three Rivers</strong>, <strong>Dowagiac</strong>, and nearby communities across the Great Lakes State and northern Indiana. Contact us to confirm service availability in your area or visit our <router-link to="/gaylord-roof-repair" class="city-link">Gaylord roofing page</router-link> for city-specific information.</p>
            </div>
        </div>
        
        <!-- Footer inside about-us to inherit background -->
        <PageFooter />
    </div>
</template>

<script>
import ContactForm from '@/components/ContactForm.vue';
import PageFooter from '@/components/PageFooter.vue';
import { upsertJsonLd, removeJsonLd } from '@/utils/seo';

export default {
    name: 'AboutUsPage',
    components: {
        ContactForm,
        PageFooter
    },
    methods: {
        openQuoteModal() {
            // Find the contact button in the floating contact form and click it
            const contactButton = document.querySelector('.floatingContact .contact-button');
            if (contactButton) {
                contactButton.click();
            }
        }
    },
    mounted() {
        upsertJsonLd('about-faq', {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'How much does a roof replacement cost in Michigan?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'The cost of a roof replacement in Michigan typically ranges from $7,000 to $25,000+ depending on the size of your home, roofing materials chosen, and complexity of the project. We offer free estimates to provide you with an accurate quote based on your specific needs.'
                    }
                },
                {
                    '@type': 'Question',
                    name: 'What\'s included in your 50-year warranty?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Our 50-year warranty covers manufacturer defects in roofing materials and workmanship. This comprehensive protection gives you peace of mind knowing your investment is protected for decades to come.'
                    }
                },
                {
                    '@type': 'Question',
                    name: 'Do you offer emergency roof repair services?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Yes. Call 269-262-5055 for emergency roof repairs and a fast response to help prevent further damage to your home.'
                    }
                },
                {
                    '@type': 'Question',
                    name: 'What roofing materials do you recommend for Michigan weather?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'For Michigan\'s harsh winters and humid summers, we prefer architectural asphalt shingles from trusted brands like CertainTeed, IKO, and Owens Corning. We also install EPDM, TPO, and modified bitumen.'
                    }
                },
                {
                    '@type': 'Question',
                    name: 'How long does a typical roof last in Michigan?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'In Michigan\'s climate, a standard asphalt shingle roof typically lasts around 30 years with proper maintenance, while higher-tier materials can last up to 50 years.'
                    }
                },
                {
                    '@type': 'Question',
                    name: 'Do you offer financing options?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Yes. We partner with financing providers to offer flexible payment plans for projects over $3,000.'
                    }
                },
                {
                    '@type': 'Question',
                    name: 'What areas do you serve?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'We proudly serve Niles, Buchanan, Benton Harbor, St. Joseph, Galien, Three Oaks, Bridgman, New Buffalo, Stevensville, Coloma, Gaylord, South Bend, Kalamazoo, Battle Creek, Portage, Three Rivers, Dowagiac, and nearby communities across Michigan and northern Indiana.'
                    }
                }
            ]
        });
    },
    beforeUnmount() {
        removeJsonLd('about-faq');
    }
};
</script>

<style scoped>
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

/* Floating Contact Bubble */
.floatingContact {
    z-index: 1000;
}

.floatingContact :deep(.contact-button) {
    position: fixed;
    bottom: max(16px, env(safe-area-inset-bottom));
    right: max(16px, env(safe-area-inset-right));
    background: linear-gradient(145deg, #a10d10 0%, #d3181c 100%);
    color: white;
    border: 2px solid rgba(241, 196, 15, 0.95);
    border-radius: 999px;
    min-width: 176px;
    max-width: 220px;
    height: 56px;
    padding: 0 18px;
    text-align: center;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
    font-weight: 800;
    letter-spacing: 0.35px;
    text-transform: uppercase;
    line-height: 1;
    white-space: nowrap;
    gap: 0;
    cursor: pointer;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.4), 0 0 0 0 rgba(241, 196, 15, 0.55);
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
    animation: ctaAttention 5.5s cubic-bezier(0.4, 0, 0.2, 1) infinite;
    z-index: 1000;
}

.floatingContact :deep(.contact-button:hover) {
    transform: translateY(-2px);
    background: linear-gradient(145deg, #b41013 0%, #e01f24 100%);
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.45), 0 0 0 6px rgba(241, 196, 15, 0.14);
}

 .floatingContact :deep(.contact-button:active) {
    transform: translateY(0);
}

.floatingContact :deep(.contact-button:focus-visible) {
    outline: 3px solid #f1c40f;
    outline-offset: 2px;
}

@media (max-width: 500px) {
    .floatingContact :deep(.contact-button) {
        min-width: 160px;
        height: 52px;
        font-size: 0.86rem;
        padding: 0 14px;
    }
}

@keyframes ctaAttention {
    0%, 72%, 100% {
        box-shadow: 0 10px 24px rgba(0, 0, 0, 0.4), 0 0 0 0 rgba(241, 196, 15, 0.55);
        transform: translateY(0);
    }
    10% {
        box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45), 0 0 0 10px rgba(241, 196, 15, 0);
        transform: translateY(-1px);
    }
    18% {
        box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45), 0 0 0 8px rgba(241, 196, 15, 0);
        transform: translateY(0);
    }
}

@media (prefers-reduced-motion: reduce) {
    .floatingContact :deep(.contact-button) {
        transition: none;
        animation: none;
    }
}

.floatingContact :deep(.financing-button) {
    display: none;
}

.about-us {
    padding: 0;
    margin: 0;
    margin-top: 93px;
    width: 100%;
    min-height: 100vh;
    background: linear-gradient(135deg, #002655 0%, #1a3a5c 50%, #4a7ba7 100%);
    color: white;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    overflow-x: hidden;
}

/* Hero Section */
.about-us h1 {
    font-size: 3em;
    color: #f1c40f;
    text-align: center;
    padding: 60px 20px 30px;
    text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.7);
    margin: 0 auto;
    display: block;
    width: 100%;
}

/* Content Container */
.about-us > * {
    max-width: 1000px;
    margin-left: auto;
    margin-right: auto;
    padding-left: 30px;
    padding-right: 30px;
    text-align: center;
}

.about-us h2 {
    font-size: 2.2em;
    color: #f1c40f;
    margin-top: 50px;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 3px solid #f1c40f;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
    text-align: center;
    display: inline-block;
    width: auto;
    min-width: 300px;
}

.about-us p {
    font-size: 1.15em;
    line-height: 1.8;
    color: #e8e8e8;
    margin-bottom: 20px;
    text-align: center;
}

.city-link {
    color: #f1c40f;
    font-weight: 700;
    text-decoration: none;
}

.city-link:hover {
    text-decoration: underline;
}

.intro {
    font-size: 1.3em;
    margin-bottom: 30px;
    padding: 25px;
    background: rgba(255, 255, 255, 0.05);
    border-left: 5px solid #f1c40f;
    border-radius: 8px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

/* Lists */
.values-list, .services-list {
    list-style-type: none;
    padding: 0;
    margin: 20px auto;
    max-width: 900px;
}

.values-list li, .services-list li {
    padding: 15px 15px 15px 45px;
    font-size: 1.15em;
    position: relative;
    margin-bottom: 12px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 8px;
    transition: all 0.3s ease;
    border-left: 3px solid transparent;
    text-align: center;
}

.values-list li:hover, .services-list li:hover {
    background: rgba(241, 196, 15, 0.1);
    border-left: 3px solid #f1c40f;
    transform: translateY(-3px);
}

.values-list li::before, .services-list li::before {
    content: "★";
    color: #f1c40f;
    position: absolute;
    left: 15px;
    font-size: 1.3em;
    top: 50%;
    transform: translateY(-50%);
}

/* CTA Section */
.cta {
    margin-top: 40px;
    margin-bottom: 40px;
    font-size: 1.3em;
    font-weight: bold;
    text-align: center;
    padding: 30px;
    background: rgba(241, 196, 15, 0.1);
    border: 2px solid #f1c40f;
    border-radius: 12px;
    box-shadow: 0 6px 20px rgba(241, 196, 15, 0.2);
    overflow: visible;
}

.cta a {
    color: #f1c40f;
    text-decoration: none;
    border-bottom: 2px solid transparent;
    transition: border-bottom 0.3s ease;
}

.cta a:hover {
    border-bottom: 2px solid #f1c40f;
}

.cta-buttons {
    display: flex;
    gap: 20px;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    margin-top: 20px;
    padding: 10px 0;
    overflow: visible;
}

.cta-phone-btn,
.cta-quote-btn {
    padding: 15px 30px;
    font-size: 1.1em;
    font-weight: bold;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    display: inline-block;
    background: #002655;
    background: linear-gradient(135deg, #002655 0%, #1a3a5c 100%);
    color: #f1c40f;
    border: 2px solid #f1c40f;
    border-bottom: 2px solid #f1c40f !important;
    box-shadow: 0 4px 15px rgba(241, 196, 15, 0.3);
    opacity: 1;
}

.cta-phone-btn:hover,
.cta-quote-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(241, 196, 15, 0.5);
    background: #1a3a5c;
    background: linear-gradient(135deg, #1a3a5c 0%, #002655 100%);
    border-bottom: 2px solid #f1c40f !important;
}

/* FAQ Section */
.faq-section {
    margin-top: 30px;
    margin-bottom: 60px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.faq-item {
    background: rgba(255, 255, 255, 0.05);
    border-left: 5px solid #f1c40f;
    padding: 25px;
    margin-bottom: 25px;
    border-radius: 8px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;
    width: 100%;
    max-width: 900px;
    text-align: center;
}

.faq-item:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(241, 196, 15, 0.3);
}

.faq-item h3 {
    color: #f1c40f;
    font-size: 1.4em;
    margin-top: 0;
    margin-bottom: 15px;
    font-weight: 600;
}

.faq-item p {
    margin: 0;
    line-height: 1.8;
    color: #e8e8e8;
    text-align: center;
}

/* Mobile Responsive */
@media (max-width: 880px) {
    .about-us {
        margin-top: 168px;
    }
    
    .about-us h1 {
        font-size: 2em;
        padding: 40px 15px 20px;
    }
    
    .about-us h2 {
        font-size: 1.6em;
        margin-top: 30px;
    }
    
    .about-us > * {
        padding-left: 20px;
        padding-right: 20px;
    }
    
    .intro {
        font-size: 1.1em;
        padding: 20px;
    }
    
    .values-list li, .services-list li {
        font-size: 1em;
        padding: 12px 12px 12px 40px;
    }
    
    .cta {
        font-size: 1.1em;
        padding: 20px;
    }
    
    .cta-buttons {
        flex-direction: column;
        gap: 15px;
    }
    
    .cta-phone-btn,
    .cta-quote-btn {
        width: 100%;
        max-width: 300px;
    }
    
    .faq-item {
        padding: 20px;
    }
    
    .faq-item h3 {
        font-size: 1.2em;
    }
}

@media (max-width: 600px) {
    .about-us h1 {
        font-size: 1.6em;
    }
    
    .about-us h2 {
        font-size: 1.4em;
    }
}

/* Override footer background to be transparent */
:deep(.footer) {
    background: transparent !important;
    background-image: none !important;
}

:deep(.payment-methods),
:deep(.contact-info) {
    background: transparent !important;
}
</style>